// Send Feedback: the UI state sent with a report (2026-10-26, docs/feedback.md).
//
// The CSR's dialog is unchanged: free text and the silent screenshot. On
// Submit the panel also sends what only the browser knows: when the contact
// was opened, the reply box's text (the server diffs it against the draft,
// redacts the changed sentences and never stores the text), the last
// Send/Process attempt in this session with the exact message shown, which
// buttons were disabled and why, each step's on-screen state, and the steps
// whose Send block the reply lifted (skippedSteps, 2026-10-27). The server
// builds everything else (server/src/services/feedback-snapshot.js).

const MAX_MESSAGE = 500

function errorCode(e) {
  const code = e?.body?.error
  return typeof code === 'string' && /^[a-z0-9_:.-]{1,80}$/i.test(code) ? code : null
}

function messageOf(e) {
  const m = e?.body?.message ?? e?.body?.detail ?? e?.message ?? null
  return typeof m === 'string' ? m.slice(0, MAX_MESSAGE) : null
}

// A refused or failed attempt. A 409 with a code is a check that blocked it
// (promise_step_unprocessed, recent_group_reply…); anything else is an error.
export function attemptFromError(action, e, at = new Date()) {
  const code = errorCode(e)
  return {
    action,
    outcome: e?.status === 409 && code ? 'blocked' : 'error',
    code,
    message: messageOf(e),
    at: at.toISOString(),
  }
}

export function attemptBlocked(action, { code = null, message = null } = {}, at = new Date()) {
  return { action, outcome: 'blocked', code, message: typeof message === 'string' ? message.slice(0, MAX_MESSAGE) : null, at: at.toISOString() }
}

export function attemptOk(action, at = new Date()) {
  return { action, outcome: 'ok', code: null, message: null, at: at.toISOString() }
}

// [{ button, reason }] for the buttons that are disabled, with the reason
// shown on screen; entries whose button is enabled are left out.
export function disabledButtons(entries) {
  return (entries ?? [])
    .filter((e) => e && e.disabled && e.button)
    .map((e) => ({ button: e.button, reason: typeof e.reason === 'string' ? e.reason.slice(0, MAX_MESSAGE) : null }))
}

// Word-level edit ratio between the draft and the box: 0 = sent as drafted,
// 1 = nothing of the draft left. (The server computes the same from the
// texts; this is the browser's own reading.)
export function editRatio(draft, box) {
  const tok = (t) => String(t ?? '').toLowerCase().split(/\s+/).filter(Boolean)
  const a = tok(draft)
  const b = tok(box)
  const n = Math.max(a.length, b.length)
  if (n === 0) return 0
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    for (let j = 1; j <= b.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    prev = cur
  }
  return Math.round((prev[b.length] / n) * 1000) / 1000
}

export function buildFeedbackUiState({ openedAt = null, draftReply = null, replyText = null, lastAttempt = null, shownErrors = [], disabled = [], stepStates = {}, checkStates = {}, skippedSteps = [] } = {}) {
  const indexes = new Set([...Object.keys(stepStates ?? {}), ...Object.keys(checkStates ?? {})].map(Number).filter(Number.isInteger))
  return {
    openedAt,
    replyText: typeof replyText === 'string' ? replyText : null,
    editRatio: typeof replyText === 'string' && typeof draftReply === 'string' ? editRatio(draftReply, replyText) : null,
    lastAttempt,
    shownErrors: (shownErrors ?? []).filter((m) => typeof m === 'string' && m.trim()).map((m) => m.slice(0, MAX_MESSAGE)).slice(0, 5),
    disabled: disabledButtons(disabled),
    steps: [...indexes].sort((x, y) => x - y).map((index) => ({
      index,
      status: stepStates?.[index]?.status ?? null,
      checkStatus: checkStates?.[index]?.status ?? null,
      error: typeof stepStates?.[index]?.error === 'string' ? stepStates[index].error.slice(0, MAX_MESSAGE) : null,
    })),
    skippedSteps: (skippedSteps ?? [])
      .filter((x) => x && Number.isInteger(x.index) && typeof x.reason === 'string')
      .map((x) => ({ index: x.index, reason: x.reason })),
  }
}
